/**
 * Centralized help messages for context-sensitive help throughout the app.
 * Edit this file to update help content in one place.
 */

export interface HelpTip {
  id: string;
  message: string;
  icon?: string;
}

export interface EmptyStateMessage {
  title: string;
  message: string;
  icon?: string;
}

/**
 * Floating help tips - shown once per user until dismissed
 */
export const helpTips: Record<string, HelpTip> = {
  // Shopping List mode tips (stock keeping + checklist)
  'stock-checklist-manage': {
    id: 'stock-checklist-manage',
    message: 'Swipe left to decrease quantity, right to increase. Items at 0 appear in Shop mode. Swipe left from 0 to set -1 (gray) for items you don\'t want to restock.',
    icon: '💡',
  },
  'stock-checklist-shop': {
    id: 'stock-checklist-shop',
    message: 'Check an item to mark it as purchased. It will increment the quantity by 1.',
    icon: '🛒',
  },
  
  // Inventory tips
  'inventory-first-visit': {
    id: 'inventory-first-visit',
    message: 'All Items shows everything in your inventory. Tap Inventory below to create a new collection. Tap [+ Add] to add items to current collection (also shows up under All Items). See Concepts under Help for a quick explanation.',
    icon: '📋',
  },
  
  // Scan tips
  'scan-first-visit': {
    id: 'scan-first-visit',
    message: 'Point your camera at items or receipts to quickly add them to your inventory.',
    icon: '📷',
  },
  
  // Collection tips
  'collection-checklist': {
    id: 'collection-checklist',
    message: 'Tap items to check them off. Great for packing lists or to-do lists!',
    icon: '✓',
  },
  'collection-stock': {
    id: 'collection-stock',
    message: 'Swipe left/right to adjust quantities. Tap the quantity badge to edit directly.',
    icon: '📦',
  },

  // Marketplace tips
  'market-proximity': {
    id: 'market-proximity',
    message: 'The distance filter uses a temporary search location — it won\'t change the location saved in your Profile. Great for searching while traveling!',
    icon: '📍',
  },

  // Recipe tips
  'recipes-library': {
    id: 'recipes-library',
    message: 'Browse recipe ideas in Library. Check a recipe to add it to your Mine collection for easy access.',
    icon: '📚',
  },
  'recipes-mine': {
    id: 'recipes-mine',
    message: 'Expand a recipe and tap "Cook this recipe" to add ingredients to your Grocery list. Tap [+ Add] to search for online recipes.',
    icon: '👨‍🍳',
  },
};

/**
 * Empty state messages - shown when a filtered view has no items
 */
export const emptyStateMessages: Record<string, EmptyStateMessage> = {
  // Shopping List empty states
  'stock-checklist-shop-empty': {
    title: 'Nothing to shop for',
    message: 'Switch to Manage and set quantity to 0 for items you need to buy.',
    icon: '🛒',
  },
  'stock-checklist-shop-all-done': {
    title: 'All done!',
    message: 'You\'ve got everything. Switch to Manage to update your shopping list.',
    icon: '✅',
  },
  
  // Recipes empty states
  'recipes-mine-empty': {
    title: 'No recipes yet',
    message: 'Go to Library tab to browse and check recipes, then return here to expand and cook them.',
    icon: '👨‍🍳',
  },

  // General inventory empty states
  'inventory-empty': {
    title: 'No items yet',
    message: 'Tap Scan to add items by taking a photo, or use the + button.',
    icon: '📦',
  },
  'inventory-search-empty': {
    title: 'No matches found',
    message: 'Try a different search term or clear the filter.',
    icon: '🔍',
  },
  'inventory-filter-empty': {
    title: 'No items match this filter',
    message: 'Try adjusting your filters or clear them to see all items.',
    icon: '🔍',
  },
  
  // Collection empty states
  'collection-empty': {
    title: 'This collection is empty',
    message: 'Add items from your inventory or scan new ones.',
    icon: '📁',
  },
  'checklist-all-done': {
    title: 'All checked!',
    message: 'Everything is checked off. Tap items to uncheck them.',
    icon: '✅',
  },
};

/**
 * Get a help tip by ID
 */
export const getHelpTip = (tipId: string): HelpTip | undefined => {
  return helpTips[tipId];
};

/**
 * Get an empty state message by ID
 */
export const getEmptyStateMessage = (messageId: string): EmptyStateMessage | undefined => {
  return emptyStateMessages[messageId];
};
